import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

type ServerTime = {
  now: string;
  date: string;
  time: string;
  timezone: string;
  abbreviation: string;
  utc_offset: string;
};

/**
 * Relógio do SERVIDOR no cabeçalho.
 *
 * ================================================================
 * POR QUE NÃO É O RELÓGIO DO NAVEGADOR
 * ================================================================
 *
 * "Alterei para enviar no horário atual pelo relógio e não enviou. Como saber
 * se a hora do servidor está correta?"
 *
 * A pergunta não tinha resposta em lugar nenhum do sistema. Todo agendamento —
 * backup, alertas, faturamento de contratos, resumo no WhatsApp — roda pelo
 * relógio do servidor, e o único relógio visível era o do celular de quem
 * pergunta.
 *
 * `new Date()` aqui seria pior que não ter relógio: mostraria a hora certa da
 * pessoa e a errada do sistema, e responderia "está tudo certo" exatamente
 * quando não está. Então a hora vem do servidor.
 *
 * ================================================================
 * COMO ELE ANDA SEM PERGUNTAR A CADA SEGUNDO
 * ================================================================
 *
 * Uma consulta a cada 5 minutos fixa a âncora; entre elas o ponteiro anda
 * localmente, somando o tempo decorrido à hora que veio de lá. Um relógio que
 * consultasse o servidor a cada segundo seriam 86 mil requisições por dia por
 * usuário, para mostrar um número que muda sozinho.
 *
 * A âncora é `Date.now()` no instante da resposta, e não a hora local formatada:
 * assim a diferença entre os dois relógios aparece de verdade, em vez de ser
 * apagada pelo próprio cálculo.
 */
export function ServerClock() {
  const { data } = useQuery({
    queryKey: ['system-time'],
    queryFn: async () => {
      const res = await api.get<{ data: ServerTime }>('/system/time');

      return { server: new Date(res.data.now).getTime(), local: Date.now(), meta: res.data };
    },
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    // Um relógio errado por alguns minutos é pior que um relógio ausente:
    // não reaproveita resposta velha de outra sessão.
    staleTime: 60 * 1000,
  });

  const [agora, setAgora] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setAgora(Date.now()), 1000);

    return () => window.clearInterval(id);
  }, []);

  if (!data) return null;

  const horaServidor = new Date(data.server + (agora - data.local));

  const relogio = horaServidor.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: data.meta.timezone,
  });

  const dataCurta = horaServidor.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    timeZone: data.meta.timezone,
  });

  /**
   * A DIVERGÊNCIA ENTRE OS DOIS RELÓGIOS, quando existe.
   *
   * Este é o número que responde a pergunta original. Abaixo de dois minutos
   * não vale mencionar — é latência de rede e arredondamento, e um aviso que
   * pisca por 40 segundos de diferença vira ruído que se aprende a ignorar.
   */
  const diferencaMin = Math.round((data.local - data.server) / 60000);
  const divergente = Math.abs(diferencaMin) >= 2;

  const titulo = divergente
    ? `Hora do servidor: ${relogio} (${data.meta.abbreviation}, UTC${data.meta.utc_offset}).\n`
      + `O relógio deste computador está ${Math.abs(diferencaMin)} min `
      + `${diferencaMin > 0 ? 'adiantado' : 'atrasado'} em relação a ele.\n\n`
      + 'Os agendamentos do sistema seguem a hora do servidor.'
    : `Hora do servidor: ${relogio} (${data.meta.abbreviation}, UTC${data.meta.utc_offset}).\n`
      + 'É por ela que rodam backup, alertas e o resumo no WhatsApp.';

  return (
    <span
      title={titulo}
      className="hidden items-center gap-1.5 rounded-lg px-2 py-1 text-sm tabular-nums text-slate-500 sm:flex dark:text-slate-400"
    >
      <span aria-hidden="true" className="text-xs opacity-70">◷</span>
      <span className="font-medium">{relogio}</span>
      <span className="text-xs opacity-70">{dataCurta}</span>

      {/* O ponto só aparece quando há divergência — um selo permanente de "ok"
          treinaria o olho a ignorar justamente o dia em que ele mudar. */}
      {divergente && (
        <span
          aria-label={`Relógio deste computador difere ${Math.abs(diferencaMin)} minutos do servidor`}
          className="h-1.5 w-1.5 rounded-full bg-amber-500"
        />
      )}
    </span>
  );
}
